import { As } from '@chakra-ui/react';
import { ReactElement } from 'react';

export type BaseAction = {
  label: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  closeOnSelect?: boolean;
  colorScheme?: string;
  tooltip?: string;
  disabledTooltip?: string;
};

type EnabledAction = {
  isDisabled?: never;
};

type DisabledAction = {
  isDisabled: boolean;
  /** @tooltip becomes required if button can be disabled */
  disabledTooltip: string;
};

export type OnClickAction = {
  /**
   * No event argument: these configs are invoked from ComplyKit press
   * engines, where React Aria delivers a PressEvent (no stopPropagation or
   * preventDefault) and presses do not bubble, so there is nothing an event
   * parameter could legitimately do. The Chakra bulk-bar engine keeps its own
   * event-ful config type (see bulk-action-bar/chakra-menu-actions.tsx).
   */
  onClick: () => void | Promise<unknown>;
};

export type IconMenuAction = { icon: As };

export type WrappedAction<T = unknown> = {
  wrapper(trigger: ReactElement, extra: T): ReactElement;
};

export type LinkAction = {
  href: string;
  isExternal?: boolean;
};

export type Action = BaseAction & (EnabledAction | DisabledAction);

type TableHeaderIconAction = Omit<Action, 'label'> & {
  label?: never;
  icon: As;
  tooltip: string;
};

type TableHeaderLabelAction = Action & {
  icon?: never;
};

export type TableHeaderAction = (TableHeaderIconAction | TableHeaderLabelAction) & OnClickAction;

export const isTableHeaderAction = (action: unknown): action is TableHeaderAction => {
  return !!action && typeof action === 'object' && ('label' in action || 'icon' in action);
};

/**
 * Menu action should always have @icon
 *
 * `colorScheme` narrows to `'red'` here: the census found no other colour on
 * a menu item, and the ComplyKit engine maps it to `tone="critical"`. Any
 * other value fails to compile instead of silently rendering neutral.
 */
export type MenuActionItem<T = unknown> = Action & { colorScheme?: 'red' } & IconMenuAction &
  (OnClickAction | WrappedAction<T> | LinkAction);

/**
 * Icon action do not require @label instead it should have @tooltip
 * It is required to have an @icon
 */
export type IconActionProps = Pick<BaseAction, 'isLoading' | 'isDisabled' | 'disabledTooltip'> & {
  icon: As;
  tooltip: string;
};
